import { Box, Button, TextField, Typography } from '@mui/material'
import jwtDecode from 'jwt-decode'
import { useEffect, FC, useState, ChangeEvent, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { IAuthJWT } from '../../config/axios'
import selectOptions from '../../constants/selectOptions'
import { AppDispatch } from '../../store'
import authSelectors from '../../store/auth/authSelectors'
import { getUserDetails } from '../../store/auth/authSlice'
import { userRoles } from '../../store/auth/userTypes'
import reportsSelectors from '../../store/reports/reportsSelectors'
import { deselectCase, getRequiresTriage, getAdminReports, getUsersReports } from '../../store/reports/reportsSlice'
import { IReport } from '../../store/reports/types'
import { IGenericObjectStringValue } from '../../types'
import renderViewUtils from '../../utils/reportsRenderView/renderViewUtils'
import BasicContainer from '../BasicContainer'
import ButtonsGroup from '../ButtonsGroup'
import CardTAG from '../CardTAG'
import Dropdown from '../Dropdown'
import PageBodyLayout from '../PageBodyLayout'

import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { filterReports, sortedData } from '../../utils'
import appConfigSelector from '../../store/appConfig/appConfigSelector'
import { caseInboxViewChange, update } from '../../store/appConfig/appConfigSlice'
import LabelErrorTAG from '../LabelErrorTAG'
import useDebounce from '../../hooks/useDebounce'

const Inbox: FC = () => {
  const [searchValue, setSearchValue] = useState<string>('')
  const debouncedSearched = useDebounce(searchValue, 500)
  const viewType = useSelector(appConfigSelector.caseInboxPageView)
  const immediateAttentionData = useSelector(reportsSelectors.immediateAttention)
  const requiresTriageData = useSelector(reportsSelectors.requiresTriage)
  const additionalInformationRequiredData = useSelector(
    reportsSelectors.additionalInformationRequired,
  )
  const userDetails = useSelector(authSelectors.userDetails)

  const assignedToMeForTriage = useSelector(reportsSelectors.assignedToMeForTriage)
  const underInvestigation = useSelector(reportsSelectors.acceptedReports)
  const userCasesIsLoading = useSelector(reportsSelectors.userCasesIsLoading)
  const requiresTriageIsLoading = useSelector(reportsSelectors.requiresTriageIsLoading)
  const authRoles = useSelector(authSelectors.userRoles)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const formMethods = useForm<IGenericObjectStringValue>({
    defaultValues: { sortBy: 'mostRecent', jumpTo: '' },
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { control, watch } = formMethods

  const sortByWatchValue = watch('sortBy')
  const jumpToWatchValue = watch('jumpTo')

  // Hide "Immediate Attention Required" entirely while it has no cases
  const showImmediateAttention = immediateAttentionData.length > 0
  const jumpToSectionOptions = selectOptions.startPageSections.filter(
    (option) => option.value !== 'immediate' || showImmediateAttention,
  )

  const filteredUnderInvestigationData = filterReports(underInvestigation, debouncedSearched)
  const filteredImmediateAttentionData = filterReports(immediateAttentionData, debouncedSearched)
  const combinedRequiresTriage = [...requiresTriageData, ...assignedToMeForTriage].filter(
    (c, i, arr) => arr.findIndex(x => x.id === c.id) === i,
  )
  const filteredRequiresTriageData = filterReports(combinedRequiresTriage, debouncedSearched)
  const filteredAdditionalInformationRequiredData = filterReports(
    additionalInformationRequiredData,
    debouncedSearched,
  )

  // useEffect(() => {

  //   if(!userDetails){
  //     const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
  //     dispatch(getUserDetails({ userId: tokenData.user_id }))
  //     dispatch(deselectCase({ value: undefined }))
  //   }

  // }, [])

  // useEffect(() => {
  //         // eslint-disable-next-line
  //   // console.log('authRoles', authRoles);

  //   if(!authRoles || authRoles.length === 0){
  //     const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
  //     dispatch(getUserDetails({ userId: tokenData.user_id }))
  //   }

  //   if (authRoles && authRoles?.length > 0) {

  //     if(authRoles.includes(userRoles.ADMIN) ) {
  //       dispatch(getAdminReports())
  //     }else{
  //       dispatch(getUsersReports())
  //     }

  //     authRoles.forEach(role => {
  //       if (role === userRoles.DESIGNATED_RECEIVER) {
  //         dispatch(getRequiresTriage())
  //       }
  //     })
  //   }
  // }, [authRoles, userDetails])

  const rolesKey = (authRoles || []).slice().sort().join('|');
  const lastRanForRolesRef = useRef<string | null>(null);

  // 1) Fetch user details once if roles are not loaded
  useEffect(() => {
    if (!authRoles || authRoles.length === 0) {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const tokenData: IAuthJWT = jwtDecode(token);
      if (lastRanForRolesRef.current === 'FETCHING_USER') return;
      lastRanForRolesRef.current = 'FETCHING_USER';
      dispatch(getUserDetails({ userId: tokenData.user_id }));
    }
  }, [authRoles, dispatch]);

  // 2) Run report fetches when roles are actually known
  useEffect(() => {
    if (!authRoles || authRoles.length === 0) return;

    if (lastRanForRolesRef.current === rolesKey) return;
    lastRanForRolesRef.current = rolesKey;

    if (authRoles.includes(userRoles.ADMIN)) {
      dispatch(getAdminReports());
    } else {
      dispatch(getUsersReports());
    }

    if (authRoles.includes(userRoles.DESIGNATED_RECEIVER)) {
      dispatch(getRequiresTriage());
    }
  }, [rolesKey, authRoles, dispatch]);



  // useEffect(() => {
  //   return () => {
  //     // reset so that on next mount, it fetches fresh data
  //     lastRanForRolesRef.current = null;
  //   };
  // }, []);

  useEffect(() => {
    if (jumpToWatchValue) {
      const section: HTMLElement | null = document.querySelector(`#${jumpToWatchValue}`)
      if (section) {
        const scrollEl = document.querySelector('.t-page-body')
        if (scrollEl) {
          scrollEl.scrollTo({
            behavior: 'smooth',
            top: section.offsetTop - 122,
          })
        }
      }
    }
  }, [jumpToWatchValue])

  const resolveRender = (data: IReport[], title: string, id: string, error = false, loading = false) => {
    return viewType === 'box'
      ? renderViewUtils.renderBoxView(data, viewType, title, id, error, loading)
      : renderViewUtils.renderListView(data, viewType, title, id, error, loading)
  }

  const newReportHandler = () => {
    const path = '/cases/newCase'
    navigate(path)
  }

  useEffect(() => {
    const viewTypeLocalStorageItem = localStorage.getItem('viewType')
    const viewTypeObject = viewTypeLocalStorageItem && JSON.parse(viewTypeLocalStorageItem)
    dispatch(update(viewTypeObject))
  }, [])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchValue(event.target.value)
  }
  return (
    <PageBodyLayout
      className={'t-cases-inbox-page'}
      dataPart={
        <>
          {showImmediateAttention &&
            resolveRender(
              sortedData(filteredImmediateAttentionData, sortByWatchValue),
              'Immediate Attention Required',
              'immediate',
              true,
              requiresTriageIsLoading || userCasesIsLoading
            )}
          {resolveRender(
            sortedData(filteredRequiresTriageData, sortByWatchValue),
            'Requires Triage',
            'requireTriage',
            false,
            requiresTriageIsLoading || userCasesIsLoading
          )}

          {resolveRender(
            sortedData(filteredUnderInvestigationData, sortByWatchValue),
            'Under Investigation',
            'underInvestigation',
            false,
            requiresTriageIsLoading || userCasesIsLoading
          )}
          {resolveRender(
            sortedData(filteredAdditionalInformationRequiredData, sortByWatchValue),
            'Additional information required',
            'additionalInformationRequired',
            false,
            requiresTriageIsLoading || userCasesIsLoading
          )}
        </>
      }
      filterPart={
        <Box
          sx={{
            maxHeight: 'calc(100vh - 150px)',
            overflowY: 'scroll',
            pr: 1,
          }}>
          <CardTAG title='Filter' className='t-filter-card'>
            <div className='t-filter-car-row t-hbox t-justify-content-between'>
              <Dropdown
                control={control}
                label='Sort by'
                options={selectOptions.filterCasesBy}
                fieldName='sortBy'
                selectedValue={sortByWatchValue}
              />
              <Box>
                <Typography sx={{ typography: 'labelBold', marginBottom: '.7rem' }}>
                  View as
                </Typography>
                <ButtonsGroup
                  selectedValue={viewType}
                  firstButtonLabel='Tiles'
                  secondButtonLabel='List'
                  firstButtonClickHandler={() => {
                    dispatch(caseInboxViewChange('box'))
                  }}
                  secondButtonClickHandler={() => {
                    dispatch(caseInboxViewChange('list'))
                  }}
                />
              </Box>
            </div>
            <div className='t-field t-vbox'>
              <LabelErrorTAG label='Search' />
              <TextField placeholder='Search' value={searchValue} onChange={handleSearchChange} />
            </div>
          </CardTAG>
          <BasicContainer>
            <Dropdown
              label='Jump to section'
              control={control}
              fieldName='jumpTo'
              selectedValue={jumpToWatchValue}
              options={jumpToSectionOptions}
              placeholder='Select a Section'
            />
          </BasicContainer>
          <Button startIcon={<AddIcon />} variant='submitFormButton' onClick={newReportHandler} sx={{
            mt: 3,
            mb: 3,
          }}>
            <Typography variant='bodyBold'>Create New Report</Typography>
          </Button>
        </Box>
      }
    />
  )
}

export default Inbox
